import { differenceInHours, format, getMinutes, parseISO } from "date-fns";
import addDays from "date-fns/addDays";
import differenceInMinutes from "date-fns/differenceInMinutes";
import { formatWithOptions } from "date-fns/fp";
import { enUS, es, fr } from "date-fns/locale";
import startOfWeek from "date-fns/startOfWeek";
import { zonedTimeToUtc } from "date-fns-tz";
import dayjs from "dayjs";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { CountryCode } from "libphonenumber-js/types"; // Type definitions for libphonenumber-js
import { useEffect, useRef } from "react";
import { findIana } from "windows-iana";

import { DUMMY_PRIORITIES_ARRAY } from "../assets/constants";
import { LeaveType } from "../components/absence-reports/store/absence-reports-types";
import { SkillLevel } from "../components/cross-skilling/types";
import {SkillInsightsDetails} from "../components/insight-dashboard/slices/insight-report-types";
import { Job } from "../components/scheduler/store/types";
import { SkillMatrix_Skill } from "../components/workers/store/manage-worker-types";
import { Skill } from "../components/workers/types";
import {
	ALPHABET,
	BOX,
	CIRCLE,
	DONUT,
	EMPTY,
	FULL,
	HALF,
	NUMBER,
	QUARTER,
	THREE_QUARTER,
} from "../design/skill-representaion-cell/constants/constants";
import { getCurrentDateFnsLocale } from "./date-locale";
import { CompanyLocation, FtShift, Priority, RoleContextType } from "./types";

const locales = { en: enUS, fr, es };

export const calculateShiftHoursAndMinutes = (
	startTime: string,
	endTime: string,
	numberOfWorkersNeeded: string | number
) => {
	let diffInMins = differenceInMinutes(new Date(endTime), new Date(startTime));

	diffInMins *= Number(numberOfWorkersNeeded);

	const hours = Math.floor(diffInMins / 60);

	const minutes = diffInMins % 60;

	return { hours, minutes };
};

export const mergeObjects = (obj1: any, obj2: any) => {
	const merged = { ...obj1 };
	// Loop through the keys of the second object
	for (const key in obj2) {
		if (Object.prototype.hasOwnProperty.call(obj2, key)) {
			if (Object.prototype.hasOwnProperty.call(merged, key)) {
				merged[key] = obj2[key];
			} else {
				merged[key] = obj2[key];
			}
		}
	}

	return merged;
};

export const getDuration = (
	startTime: string,
	endTime: string,
	unpaidBreakMins: number
): string => {
	const [hours1, minutes1] = startTime.split(":").map(Number);
	const [hours2, minutes2] = endTime.split(":").map(Number);

	const date1 = new Date(0, 0, 0, hours1, minutes1);
	const date2 = new Date(0, 0, 0, hours2, minutes2);

	if (date2 < date1) {
		date2.setDate(date2.getDate() + 1);
	}

	const differenceInMinutes = (date2.getTime() - date1.getTime()) / (1000 * 60);

	const hours = (differenceInMinutes - unpaidBreakMins) / 60;
	const finalHours = hours.toFixed(2);

	return finalHours;
};

export const createArrayUpToNumber = (n: number): number[] => {
	return Array.from({ length: n }, (_, index) => index + 1);
};

export const isTooDark = (color: string): boolean => {
	let r: number, g: number, b: number;
	// Check the format of the color, HEX or RGB?
	if (color.match(/^rgb/)) {
		// If HEX --> store the red, green, blue values in separate variables
		const match = color.match(
			/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
		);

		if (match) {
			r = parseInt(match[1]);
			g = parseInt(match[2]);
			b = parseInt(match[3]);
		} else {
			throw new Error("Invalid RGB color format");
		}
	} else {
		// If RGB --> Convert it to HEX
		const shouldReplace = color.length < 5;
		let hexColor = 0;

		if (shouldReplace) {
			hexColor = +("0x" + color.slice(1).replace(/./g, "$&$&"));
		} else {
			hexColor = +("0x" + color.slice(1));
		}

		r = hexColor >> 16;
		g = (hexColor >> 8) & 255;
		b = hexColor & 255;
	}

	// HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
	const hsp: number = Math.sqrt(
		0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b)
	);

	// Using the HSP value, determine whether the color is light or dark
	return hsp < 139.5;
};

export const getRepType = (code: string | undefined) => {
	try {
		const codeArr = code ? code.split("-") : [];
		let returnValue = DONUT;
		if (codeArr.length > 0) {
			switch (codeArr[0]) {
				case CIRCLE:
					returnValue = CIRCLE;
					break;
				case BOX:
					returnValue = BOX;
					break;
				case ALPHABET:
					returnValue = ALPHABET;
					break;
				case NUMBER:
					returnValue = NUMBER;
					break;
				default:
					break;
			}
			return returnValue;
		} else {
			return DONUT;
		}
	} catch (e) {
		return DONUT;
	}
};
export const getRepLevel = (code: string | undefined, level = 1) => {
	try {
		let checkLevel: string | number = level;
		let returnValue = EMPTY;
		if (code) {
			const codeArr = code ? code.split("-") : [];
			if (codeArr.length > 2) {
				// To handle three-quarter string
				checkLevel = `${codeArr[1]}-${codeArr[2]}`;
			} else if (codeArr.length > 0) {
				checkLevel = codeArr[1];
			}
		}
		switch (checkLevel) {
			case 1:
			case QUARTER:
				returnValue = QUARTER;
				break;
			case 2:
			case HALF:
				returnValue = HALF;
				break;
			case 3:
			case THREE_QUARTER:
				returnValue = THREE_QUARTER;
				break;
			case 4:
			case FULL:
				returnValue = FULL;
				break;
			default:
				break;
		}
		return returnValue;
	} catch (e) {
		return EMPTY;
	}
};

export const getConfirmedWorkersCount = (shift: any): number => {
	if (!shift) return 0;
	const fullSlots = shift.fullSlots ?? 0;
	const firstHalfSlots = shift.firstHalfSlots ?? 0;
	const secondHalfSlots = shift.secondHalfSlots ?? 0;

	// Formula: fullSlots + min(firstHalfSlots, secondHalfSlots)
	// But actually the user said: "if firstHalfSlots is 2 and secondHalfSlots is 5 and fullSlots is 1 then confirmed workers count should be 1 + 2 = 3"
	// This matches fullSlots + min(firstHalfSlots, secondHalfSlots).
	// But if one of them is 0, e.g. firstHalfSlots=2, secondHalfSlots=0, fullSlots=1, then 1 + 0 = 1.
	// This seems correct as a "combination" of first and second half makes one full person.

	if (
		shift.fullSlots !== undefined ||
		shift.firstHalfSlots !== undefined ||
		shift.secondHalfSlots !== undefined
	) {
		return fullSlots + Math.min(firstHalfSlots, secondHalfSlots);
	}

	return shift.slotsFilled ?? 0;
};

export const getTotalWorkersCount = (
	workersCount: number,
	outOfWorkersCount: number
): number => {
	let returnValue = outOfWorkersCount ?? 0;
	try {
		if (workersCount) {
			if (workersCount > outOfWorkersCount) {
				returnValue = workersCount;
			}
		}
		return returnValue;
	} catch (e) {
		return returnValue;
	}
};

export const getHourMinuteDifference = (
	startDate: Date,
	endDate: Date
): {
	differenceInText: string;
	differenceInMinutes: number;
} => {
	const returnValue = {
		differenceInText: "0 hour",
		differenceInMinutes: 0,
	};
	if (startDate.getTime() - endDate.getTime() <= 0) {
		return returnValue;
	}
	const timeDifference = Math.abs(startDate.getTime() - endDate.getTime());
	returnValue.differenceInMinutes = Math.ceil(timeDifference / (1000 * 60));

	const hours = Math.floor(timeDifference / (1000 * 60 * 60));
	const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

	if (hours === 0) {
		returnValue.differenceInText = `${minutes} minute${
			minutes !== 1 ? "s" : ""
		}`;
	} else {
		if (minutes === 0) {
			returnValue.differenceInText = `${hours} hour${hours !== 1 ? "s" : ""}`;
		} else {
			returnValue.differenceInText = `${hours} hour${
				hours !== 1 ? "s" : ""
			} ${minutes} minute${minutes !== 1 ? "s" : ""}`;
		}
	}

	return returnValue;
};

export default function isValidPhoneNumberForCountry(
	phoneNumberString: string,
	country: string
): boolean {
	try {
		if (phoneNumberString.length > 2) {
			const phoneNumber: any = parsePhoneNumberWithError(phoneNumberString, {
				defaultCountry: country as CountryCode,
				extract: false,
			});
			if (!phoneNumber) {
				return false;
			}
			if (phoneNumber.country !== country) {
				return false;
			}
			return phoneNumber.isValid();
		} else {
			return true;
		}
	} catch (error) {
		return true;
	}
}

export const deCamelCase = (text: string): string => {
	try {
		if (text.includes(" ")) return text;
		const result = text.replace(/([A-Z])/g, " $1");
		return result.charAt(0).toUpperCase() + result.slice(1);
	} catch (e) {
		return text;
	}
};

export const capitalizeFirstLetter = (
	value: string,
	ofFullSentence = false
) => {
	let returnValue = value;
	if (ofFullSentence) {
		const valueArray = value.toLowerCase().split(" ");
		const newValueArray = valueArray.map((word) => {
			return word.charAt(0).toUpperCase() + word.slice(1);
		});
		returnValue = newValueArray.join(" ");
	} else {
		returnValue = value.charAt(0).toUpperCase() + value.slice(1);
	}
	return returnValue;
};

export const getLevelObj = (value = "0", allSkillLevels: SkillLevel[] = []) => {
	// Based on skill levels provided return info about level based on level value
	const returnValue = {
		repType: DONUT,
		repLevel: EMPTY,
		levelValue: value,
		backgroundColor: "",
		foregroundColor: "",
		name: "",
	};

	const level = allSkillLevels.find(
		(sLevel) => sLevel.level === parseInt(value)
	);

	if (level) {
		returnValue.repType = getRepType(level.repCode);
		returnValue.repLevel = getRepLevel(level.repCode, level.level);
		returnValue.levelValue = String(level.value);
		returnValue.backgroundColor = level.backColorCode ?? "";
		returnValue.foregroundColor = level.foreColorCode ?? "";
		returnValue.name = level.name;
	}

	return returnValue;
};

export const getTimeForTimePicker = (newValue: any) => {
	try {
		let UTCDateString = JSON.stringify(newValue);
		UTCDateString = UTCDateString.slice(1, UTCDateString.length - 1);
		const newLocalDate = new Date(UTCDateString);
		let hours = `${newLocalDate.getHours()}`;
		let minutes = `${newLocalDate.getMinutes()}`;
		let seconds = `${newLocalDate.getSeconds()}`;
		if (hours.length === 1) {
			hours = `0${hours}`;
		}
		if (minutes.length === 1) {
			minutes = `0${minutes}`;
		}
		if (seconds.length === 1) {
			seconds = `0${seconds}`;
		}
		return `${hours}:${minutes}`;
	} catch (e) {
		// Do nothing
		return "08:00";
	}
};

export const useIsComponentMounted = () => {
	const isComponentMounted = useRef(false);

	useEffect(() => {
		isComponentMounted.current = true;
		return () => {
			isComponentMounted.current = false;
		};
	}, []);

	return isComponentMounted;
};

export const addId = (arr: any) =>
	arr.map((obj: any, index: number) => ({ ...obj, idx: index + 1 }));

export const locationOfWorkcenter = (
	workcenterId: number,
	locations: CompanyLocation[]
) => {
	try {
		const location = locations.find((location) =>
			location.workCenters.some((wc) => wc.id === workcenterId)
		);
		return location ? location.name : "";
	} catch (e) {
		return "";
	}
};

export const addSkillsToShifts = (allShiftData: any) => {
	try {
		// Shift by skills related changes
		// appending skills array object to shift
		const shiftsData: any = [];
		const shiftsBySkills = allShiftData.skills ?? [];
		const allShiftsArray = allShiftData.signupTracking ?? [];
		allShiftsArray.forEach((shiftObject: any) => {
			shiftsData.push({
				...shiftObject,
				skills: shiftsBySkills.filter(
					(shiftBySkill: any) => shiftBySkill.shiftId === shiftObject.id
				),
				shiftSkills: shiftsBySkills.filter(
					(shiftBySkill: any) => shiftBySkill.shiftId === shiftObject.id
				),
			});
		});
		return shiftsData;
	} catch (e) {
		return [];
	}
};

//Add date time as per the api specification to a bunch of shifts in an array
export const addStartDateEndDate = (allShiftsData: any) => {
	if (allShiftsData) {
		return allShiftsData.map((shift: any) => {
			/*
                        TODO: To activate timezones
                        let startDate = `${shift.startDateTime}.000Z`;
                        let endDate = `${shift.endDateTime}.000Z`;
                        */
			const startDate = shift.startDateTime;
			const endDate = shift.endDateTime;
			shift.startDate = startDate;
			shift.endDate = endDate;
			return shift;
		});
	} else {
		return [];
	}
};

// Add date time as per the api specification to a single shifts
export const addStartDateEndDateToAShift = (shift: any) => {
	const startDate = shift.startDateTime;
	const endDate = shift.endDateTime;
	shift.startDate = startDate;
	shift.endDate = endDate;
	return shift;
};

function getDayNumber(dayName: string): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
	const days: Record<string, 0 | 1 | 2 | 3 | 4 | 5 | 6> = {
		sunday: 0,
		monday: 1,
		tuesday: 2,
		wednesday: 3,
		thursday: 4,
		friday: 5,
		saturday: 6,
	};
	return days[dayName?.toLowerCase()] ?? 1;
}

export const generateDaysOfTheWeek = (seedDate: any, weekStartDay: string) => {
	const weekStartDayNumber = getDayNumber(weekStartDay);

	const monday: any = startOfWeek(seedDate, {
		weekStartsOn: weekStartDayNumber ?? 1,
	});
	const tuesday = addDays(monday, 1);
	const wednesday = addDays(monday, 2);
	const thursday = addDays(monday, 3);
	const friday = addDays(monday, 4);
	const saturday = addDays(monday, 5);
	const sunday = addDays(monday, 6);

	return {
		monday,
		tuesday,
		wednesday,
		thursday,
		friday,
		saturday,
		sunday,
	};
};

export const generateDaysOfTheWeekSerialized = (
	seedDate: any,
	weekStartDay: string
) => {
	const daysOfTheWeek: any = generateDaysOfTheWeek(seedDate, weekStartDay);
	return Object.keys(daysOfTheWeek).reduce((acc: any, key) => {
		acc[key] = daysOfTheWeek[key].toISOString();
		return acc;
	}, {});
};

type DateFormat =
	| "DATE_RANGE"
	| "SHORT_DATE"
	| "CAPTION_DATE"
	| "NUMERIC_DATE"
	| "TIME"
	| "TIME_RANGE"
  | "MILITARY_TIME_RANGE"
	| "DATE_WITH_TIME";

function formatHourConditionally(date: Date): string {
  if (getMinutes(date) === 0) {
    // If minutes are zero, format without minutes
    return format(date, "H"); // 'H' gives 0-23 hour
    // Or use 'k' for 1-24 hour if preferred: format(date, 'k')
  } else {
    // If minutes are non-zero, format with minutes
    return format(date, "H:mm"); // 'H:mm' gives 0-23 hour and 00-59 minutes
    // Or use 'k:mm' for 1-24 hour: format(date, 'k:mm')
  }
}

export const formatDate = ({
	dateStr,
	endDateStr,
	formatType,
	ISOString = true,
}: {
	dateStr: string;
	endDateStr?: string;
	formatType: DateFormat;
	ISOString?: boolean;
}): string => {
	let formattedDate = "";

	if (!ISOString) {
		// Using dayjs for date formatting
		switch (formatType) {
			case "NUMERIC_DATE": {
				formattedDate = dayjs(dateStr).format("MM/DD/YYYY");
				break;
			}
		}
	} else {
		// Month and weekday names have to follow the UI language; the numeric
		// cases below are language-independent and stay as they are.
		const locale = getCurrentDateFnsLocale();
		switch (formatType) {
			case "DATE_RANGE": {
				if (!endDateStr) {
					throw new Error("End date is required for DATE_RANGE format");
				}
				if (!dateStr || !endDateStr || isNaN(new Date(dateStr).getTime()) || isNaN(new Date(endDateStr).getTime())) return "";
				formattedDate = `${format(new Date(dateStr), "d MMM yyyy", {
					locale,
				})} - ${format(new Date(endDateStr), "d MMM yyyy", { locale })}`;
				break;
			}
			case "SHORT_DATE": {
				if (!dateStr || isNaN(new Date(dateStr).getTime())) return "";
				formattedDate = format(new Date(dateStr), "EEE, d MMM", { locale });
				break;
			}

			case "CAPTION_DATE": {
				if (!dateStr || isNaN(new Date(dateStr).getTime())) return "";
				formattedDate = format(new Date(dateStr), "EEE, MMM, d yyyy", {
					locale,
				});
				break;
			}

			case "NUMERIC_DATE": {
				if (!dateStr || isNaN(new Date(dateStr).getTime())) return "";
				formattedDate = format(new Date(dateStr), "MM/dd/yyyy");
				break;
			}
			case "TIME_RANGE": {
				if (!endDateStr) {
					throw new Error("End date is required for DATE_RANGE format");
				}
				if (!dateStr || !endDateStr || isNaN(new Date(dateStr).getTime()) || isNaN(new Date(endDateStr).getTime())) return "";
				const startTime = format(new Date(dateStr), "h:mm a");
				const endTime = format(new Date(endDateStr), "h:mm a");
				formattedDate = `${startTime} - ${endTime}`;
				break;
			}

      case "MILITARY_TIME_RANGE": {
        if (!endDateStr) {
          throw new Error("End date is required for DATE_RANGE format");
        }
        if (!dateStr || !endDateStr || isNaN(new Date(dateStr).getTime()) || isNaN(new Date(endDateStr).getTime())) return "";
        const startTime = formatHourConditionally(new Date(dateStr));
        const endTime = formatHourConditionally(new Date(endDateStr));
        formattedDate = `${startTime} - ${endTime}`;
        break;
      }

			case "TIME": {
				if (!dateStr || isNaN(new Date(dateStr).getTime())) return "";
				formattedDate = format(new Date(dateStr), "h:mm a");
				break;
			}

			case "DATE_WITH_TIME": {
				if (!dateStr || isNaN(new Date(dateStr).getTime())) return "";
				formattedDate = `${format(new Date(dateStr), "EEE, d MMM h:mm a", {
					locale,
				})}`;
				break;
			}

			default:
				throw new Error("Invalid format type");
		}
	}
	return formattedDate;
};

/**
 * Formats a worker's last-updated timestamp for display, e.g. "8/2/2026 9:30 PM".
 * The BE sends an ISO string carrying a UTC offset (e.g.
 * "2026-08-02T21:30:00+05:30"); we convert that instant to the viewer's local
 * (browser) timezone. Returns "" for missing/invalid values.
 */
export const formatWorkerLastUpdated = (
	isoString?: string | null
): string => {
	if (!isoString) {
		return "";
	}
	const date = new Date(isoString);
	if (isNaN(date.getTime())) {
		return "";
	}
	return format(date, "M/d/yyyy h:mm a");
};

export const appendPriorityToSkills = (
	skills: SkillMatrix_Skill[],
	priorities: Priority[]
) => {
	return skills.map((skill: SkillMatrix_Skill) => {
		let selectedPriority = priorities.find(
			(priority) => priority.id === skill.priorityId
		);
		if (selectedPriority === undefined) {
			if (priorities.length > 0) {
				selectedPriority =
					priorities.find((priority) => priority.isDefault) ?? priorities[0];
			} else {
				selectedPriority = DUMMY_PRIORITIES_ARRAY[0];
			}
		}
		return { ...skill, priority: selectedPriority };
	});
};
export const formatSkillsByJobs = (
	allJobs: Job[],
	jobIdsSkillsArr: Skill[],
	jobIds: number[],
	filteredSkills: Skill[] = []
): void => {
	allJobs.forEach((job) => {
		if (jobIds.includes(job.id)) {
			job.skills.forEach((sk: Skill) => {
				try {
					if (filteredSkills.length > 0) {
						filteredSkills.forEach((filteredSkill) => {
							const filteredSkillId = filteredSkill.skillId ?? filteredSkill.id;
							const jobSkillId = sk.skillId ?? sk.id;
							if (filteredSkillId === jobSkillId) {
								jobIdsSkillsArr.push({
									...sk,
									jobName: job.name,
									jobColor: job.hexColor,
									jobId: job.id,
								});
							}
						});
					} else {
						jobIdsSkillsArr.push({
							...sk,
							jobName: job.name,
							jobColor: job.hexColor,
							jobId: job.id,
						});
					}
				} catch (e) {
					jobIdsSkillsArr.push({
						...sk,
						jobName: job.name,
						jobColor: job.hexColor,
						jobId: job.id,
					});
				}
			});
		}
	});
};

export const parseCalcExpression = (expression: string): number => {
	// Use a regular expression to extract values
	const regex = /(\d+)(vh|px)/g;
	let match;
	let totalPixels = 0;

	while ((match = regex.exec(expression)) !== null) {
		const value = parseFloat(match[1]);
		const unit = match[2];

		if (unit === "vh") {
			// Convert vh to pixels (assuming 100vh = window.innerHeight)
			totalPixels += (value / 100) * window.innerHeight;
		} else if (unit === "px") {
			totalPixels += value;
		}
	}

	return totalPixels;
};

export const parseNumberFromPx = (value: string): number => {
	return parseInt(value.replace("px", ""));
};

export function truncateStringToFitBox(str: string, maxWidth: number) {
	if (str.length <= maxWidth) {
		return str;
	}

	const ellipsis = "...";
	const charsToShow = maxWidth - ellipsis.length;
	const frontChars = Math.ceil(charsToShow / 2);
	const backChars = Math.floor(charsToShow / 2);

	const truncatedString =
		str.slice(0, frontChars) + ellipsis + str.slice(str.length - backChars);

	return truncatedString;
}

export const calculateLeaveHours = (
	startDateTime: string,
	endDateTime: string
) => {
	const start = parseISO(startDateTime);
	const end = parseISO(endDateTime);

	return differenceInHours(end, start);
};

export const formatDurationInHoursAndMinutes = (start: string | number, end: string | undefined) => {
    let totalMinutes;
    let isNegative = false;

    // Use a switch statement to determine how to process the input
    switch (true) {
        // Case 1: Input is a single number (in hours)
        case typeof start === "number" && end === undefined: {
            let hoursInput = start;
            if (hoursInput < 0) {
                isNegative = true;
                hoursInput = Math.abs(hoursInput);
            }
            totalMinutes = Math.round(hoursInput * 60);
            break;
        }

        // Case 2: Input is two date-time strings
        case typeof start === "string" && typeof end === "string": {
            const startDate = parseISO(start);
            const endDate = parseISO(end);

            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                return "Invalid date format";
            }

            let diff = differenceInMinutes(endDate, startDate);
            if (diff < 0) {
                isNegative = true;
                diff = Math.abs(diff);
            }
            totalMinutes = diff;
            break;
        }

        // Default case for any other invalid combination of arguments
        default:
            return "Invalid input: Provide a single number (hours) or two date-time strings.";
    }

    if (totalMinutes === 0) {
        return "0 hours";
    }

    // --- Simplified Formatting Logic (Hours and Minutes Only) ---
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const pluralize = (count: number, singular: string) => {
        if (count === 0) return "";
        return `${count} ${singular}${count > 1 ? "s" : ""}`;
    };

    const hourString = pluralize(hours, "hour");
    const minuteString = pluralize(minutes, "minute");

    const result = [hourString, minuteString].filter(Boolean).join(" ");

    return isNegative ? `-${result}` : result;
};

export const hasPermission = (
	roles: RoleContextType[],
	permissionPath: string
): boolean => {
	try {
		const [section, permission] = permissionPath.split(".");
		return roles.some(
			(role) => role.permissions?.[section]?.[permission] === true
		);
	} catch (e) {
		return true;
	}
};

// Evaluation feature RBAC is driven entirely by the dotted permission strings
// returned in the /Users/authenticate response (e.g.
// "settings.evaluation_setup.plan.add"). Roles are not consulted — a
// permission is granted iff its string is present in the array.
export const hasEvaluationPermission = (
	permissions: string[] | undefined,
	permissionString: string
): boolean => {
	if (!permissions?.length || !permissionString) return false;
	return permissions.includes(permissionString);
};

/**
 * Filters shifts based on the worker's time zone.
 * @param shifts - Array of shift objects with StartDateTime and EndDateTime in ISO format.
 * @param workerTimeZone - Time zone of the worker (e.g., 'Central Standard Time').
 * @param shiftMetaData
 * @returns Filtered shifts that start within the worker's day.
 */
export const filterShiftsByWorkerDay = (
	shifts: FtShift[] = [],
	workerTimeZone = "Central Standard Time",
	shiftMetaData: { startDateTime: string; endDateTime: string }
): FtShift[] => {
	try {
		const shiftStartDateTime = shiftMetaData.startDateTime;

		// Map Windows time zone to IANA time zone
		const ianaZones = findIana(workerTimeZone);
		if (!ianaZones.length) {
			throw new Error(
				`Could not map Windows time zone "${workerTimeZone}" to IANA time zone.`
			);
		}
		const ianaTimeZone = ianaZones[0]; // Use the first matching IANA time zone

		// Define worker's day start and end
		const workerDayStart = new Date(shiftStartDateTime); // Adjusted time zone based on worker
		workerDayStart.setHours(0, 0, 0, 0); // Worker day starts at 00:00
		const workerDayEnd = new Date(workerDayStart);
		workerDayEnd.setHours(24, 0, 0, 0); // Worker day ends at 24:00

		// Convert the worker's day start to UTC
		const utcStart = zonedTimeToUtc(workerDayStart, ianaTimeZone);
		const utcEnd = zonedTimeToUtc(workerDayEnd, ianaTimeZone);

		// Filter shifts that start within the worker's day
		return shifts.filter(({ StartDateTime }) => {
			const shiftStart = new Date(StartDateTime);
			return shiftStart >= utcStart && shiftStart < utcEnd; // Only include shifts that start within the worker's day
		});
	} catch (e) {
		return [];
	}
};

export const formatFullTimeShifts = (
	shifts: FtShift[],
	shiftPatternName = ""
): string => {
	try {
		const groupedShifts = shifts.reduce((acc, shift) => {
			const formattedShift = `${format(
				new Date(shift.StartDateTime),
				"h:mm a"
			)} - ${format(new Date(shift.EndDateTime), "h:mm a")}`;
			(acc[shift.ShiftName] ||= []).push(formattedShift);
			return acc;
		}, {} as Record<string, string[]>);

		const groupedShiftsArray = Object.entries(groupedShifts);
		const sortedShifts =
			groupedShiftsArray
				.sort(([shiftNameA], [shiftNameB]) =>
					shiftNameA.localeCompare(shiftNameB)
				)
				.map(([shiftName, shiftTimes], index) => {
					const sortedShiftTimes = shiftTimes.sort();
					return `${
						groupedShiftsArray.length > 1
							? index === 0
								? `- ${shiftName} `
								: `${shiftName} `
							: `${shiftPatternName} `
					}(${sortedShiftTimes.join(", ")})`;
				}) ?? [];

		return sortedShifts.length > 0
			? `${sortedShifts.join(", ")}`
			: shiftPatternName
			? `${shiftPatternName} (OFF)`
			: "-";
	} catch (e) {
		return "-";
	}
};

export const normalizeSkillInsightsResponse = (data: any): SkillInsightsDetails[] => {
	return data.map((item: any) => ({
		skillId: item.skillId,
		skillName: item.skillName,
		locationIds: item.locationIds,
		interestedWorkersCount: (item.interestedWorkersCount || []).map((i: any) => ({
			locationId: i.lId !== undefined && i.lId !== null ? i.lId : i.locationId,
			count: i.c !== undefined && i.c !== null ? i.c : i.count,
		})),
		workersSkillLevel: (item.workersSkillLevel || []).map((w: any) => ({
			locationId: w.lId !== undefined && w.lId !== null ? w.lId : w.locationId,
			workerCount: (w.wc || w.workerCount || []).map((wc: any) => ({
				level: wc.l !== undefined && wc.l !== null ? wc.l : wc.level,
				count: wc.c !== undefined && wc.c !== null ? wc.c : wc.count,
			})),
		})),
		priorityId: item.priorityId,
	}));
};


export  const formatSkillScore = (skillScore: string | null | undefined | number) => {
  try {
    if (skillScore === undefined || skillScore === null || skillScore === "") {
      return ""; // Return an empty string if skillScore is undefined, null, or an empty string
    }

    // Ensure skillScore is treated as a number
    const parsedScore =
      typeof skillScore === "number" ? skillScore : parseFloat(skillScore);
    if (isNaN(parsedScore)) {
      return ""; // Return an empty string if parsing fails
    }

    // Format the number to 2 decimal places and remove .00 if the decimal part is zero
    const formattedScore = parsedScore.toFixed(2);
    return formattedScore.endsWith(".00")
      ? parsedScore.toFixed(0)
      : formattedScore;
  } catch (e) {
    return "";
  }
};

export const appendWorkCentersIds = (locations: any, workCenters: any) => {
	return locations.map((location: any) => {
		const workCentersForLocation = workCenters.filter(
			(workCenter: any) => workCenter.locationId === location.id
		);
		return {
			...location,
			workCenters: workCentersForLocation.map(
				(workCenter: any) => workCenter.id
			),
		};
	});
};

export const parseSeniority = (seniority: string) => {
	try {
		const years = parseInt(
			seniority.match(/(\d+)\s*years?/i)?.[1] || "0",
			10
		);
		const months = parseInt(
			seniority.match(/(\d+)\s*months?/i)?.[1] || "0",
			10
		);
		const days = parseInt(
			seniority.match(/(\d+)\s*days?/i)?.[1] || "0",
			10
		);

		// Convert to total days (assuming 1 year = 365 days, 1 month = 30 days)
		return years * 365 + months * 30 + days;
	} catch (e) {
		return 0;
	}
};

export const getFormattedDate = (
  language: string,
  date: string | Date,
  format: any
) => {
  let selectedLanguage: "en" | "fr" | "es" = "en";
  if (language === "fr") {
    selectedLanguage = "fr";
  } else if (language === "es") {
    selectedLanguage = "es";
  } else if (language === "en") {
    selectedLanguage = "en";
  }
  const locale: Locale = locales[selectedLanguage] || enUS;

  const formattedText = formatWithOptions({ locale })(format, new Date(date));

  return formattedText || "";
};

// Covert 03:00:00 to 3 hours in milliseconds (example)
export const convertTimeToMilliseconds = (time: string): number => {
  const [hours, minutes, seconds] = time.split(":").map(Number);

  return (
    (hours || 0) * 3600000 + // Convert hours to milliseconds
    (minutes || 0) * 60000 + // Convert minutes to milliseconds
    (seconds || 0) * 1000 // Convert seconds to milliseconds
  );
};

export const formatDurationAbbreviated = (hours: number): string => {
  if (hours <= 0) return "0 h";
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  const parts = [];
  if (h > 0) parts.push(`${h} h`);
  if (m > 0) parts.push(`${m} m`);

  return parts.length > 0 ? parts.join(" ") : "0 h";
};

export const formatDurationFull = (hours: number): string => {
  if (hours <= 0) return "0 Minutes";
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  const parts = [];
  if (h > 0) parts.push(`${h} Hour${h !== 1 ? "s" : ""}`);
  if (m > 0) parts.push(`${m} Minute${m !== 1 ? "s" : ""}`);

  return parts.length > 0 ? parts.join(" ") : "0 Minutes";
};

export const formatDuration = (hours: number): string => {
    if (hours <= 0) return "0 minutes";

    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    const hourString = h > 0 ? `${h} hour${h > 1 ? "s" : ""}` : "";
    const minuteString = m > 0 ? `${m} minute${m > 1 ? "s" : ""}` : "";

    return [hourString, minuteString].filter(Boolean).join(" ");
};

export const getLeaveTypeNames = (leaveTypes: LeaveType[]): string[] => {
	if (!leaveTypes || leaveTypes.length === 0) {
		return [];
	}

	return leaveTypes.map(lt => lt?.leaveTypeName ?? "-");
  };

/**
 * Generates a formatted date range text (e.g., "NOV 10 - NOV 16, 2025") for a week.
 *
 * @param date - The date within the week to calculate the range for. Can be a Date object, string, or number.
 * @param hideYear - If true, the year is omitted from the range unless the start and end dates are in different years.
 * @param weekStartDay
 * @param locale - date-fns locale for the month name; defaults to English when omitted.
 * @returns The formatted date range string or an empty string if an error occurs.
 */
export const getDateRangeText = (date: Date | string | number, weekStartDay = "monday", hideYear = false, locale?: Locale): string => {
  try {
    // Type inference suggests thisWeekDates is Date[] if selectWeek is typed correctly
    const thisWeekDates = selectWeek(date, weekStartDay);

    if (thisWeekDates.length === 0) {
      return "";
    }

    const firstDate = thisWeekDates[0];
    const lastDate = thisWeekDates[thisWeekDates.length - 1];

    /**
     * Helper function to format a single date.
     * @param date - The date object.
     * @param includeYear - Whether to include the year in the output string.
     * @returns The formatted date string (e.g., "NOV 10" or "NOV 10, 2025").
     */
    const formatDate = (date: Date, includeYear: boolean): string => {
      // format() returns a string
      const month: string = format(date, "LLL", { locale }).toUpperCase();
      const day: string = format(date, "dd");
      const year: string = includeYear ? `, ${format(date, "yyyy")}` : "";
      return `${month} ${day}${year}`;
    };

    // format() returns a string, so these variables are strings
    const sameMonth: boolean = format(firstDate, "LLL", { locale }) === format(lastDate, "LLL", { locale });
    const sameYear: boolean = format(firstDate, "yyyy") === format(lastDate, "yyyy");

    // For first date, only include year if different years and not hidden and not the same month
    const showFirstYear: boolean = !sameYear && !hideYear && !sameMonth;
    // For last date, include year if not hidden
    const showLastYear = !hideYear;

    const firstDateString: string = formatDate(firstDate, showFirstYear);
    const lastDateString: string = formatDate(lastDate, showLastYear);

    return `${firstDateString} - ${lastDateString}`;
  } catch (e) {
    // Catches any error, including runtime errors if selectWeek or format fail
    return "";
  }
};

const selectWeek = (date: string | number | Date, weekStartDay: string) => {
  try {
    const inputDate = new Date(date);

    const weekDays = generateDaysOfTheWeek(inputDate, weekStartDay);
    const daysArray = Object.entries(weekDays)
      .sort(([a], [b]) => {
        const order = [
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ];
        return order.indexOf(a) - order.indexOf(b);
      })
      .map(([_, value]) => value);
    return daysArray;
  } catch (e) {
    return [];
  }
};

export const isSingleDate = (
  date: Date | { startDate: Date; endDate: Date }
): date is Date => {
  return date instanceof Date;
};
